import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { authApi } from '@/services/api'
import { setToken } from '@/lib/utils'
import { toast } from 'sonner'
import { Eye, EyeOff, Mail, Lock, User, Ticket, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'

export function SignupPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [referrer, setReferrer] = useState('')
  const [inviteOnly, setInviteOnly] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [requiredInviteEmail, setRequiredInviteEmail] = useState('')
  const [checkingConfig, setCheckingConfig] = useState(true)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const updateInviteCode = (value) => {
    setInviteCode(value)
    setInviteError('')
    setRequiredInviteEmail('')
  }

  const updateEmail = (value) => {
    setEmail(value)
    setInviteError('')
    setRequiredInviteEmail('')
  }

  // Auto-fill invite code from URL ?invite=CODE and referrer from ?referrer=...
  useEffect(() => {
    const codeFromUrl = searchParams.get('invite')
    if (codeFromUrl) {
      setInviteCode(codeFromUrl)
    }

    const referrerFromUrl = searchParams.get('referrer')
    if (referrerFromUrl) {
      setReferrer(referrerFromUrl)
    }

    // Check if site is invite-only
    const checkSiteConfig = async () => {
      try {
        const response = await authApi.getSiteConfig()
        setInviteOnly(response.data?.invite_only_registration || false)
      } catch (error) {
        console.error('Failed to check site config:', error)
      } finally {
        setCheckingConfig(false)
      }
    }

    checkSiteConfig()
  }, [searchParams])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields')
      return
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (password.length < 10) {
      toast.error('Password must be at least 10 characters')
      return
    }

    if (!agreeToTerms) {
      toast.error('Please agree to the terms and conditions')
      return
    }

    if (inviteOnly && !inviteCode) {
      toast.error('Invite code is required for registration')
      return
    }

    setLoading(true)
    try {
      const response = await authApi.signup({
        first_name: name,
        email,
        password,
        invite_code: inviteCode,
        referrer
      })

      // Store user alias for verification
      if (response.data.alias) {
        localStorage.setItem('user', response.data.alias)
      }

      // Check if email verification is required
      if (!response.data.confirmed) {
        toast.success('Account created! Please check your email for verification code.')
        navigate('/email/verify')
      } else {
        // User is already confirmed (shouldn't happen for new signups)
        if (response.data.access) {
          setToken(response.data.access)
        }
        const redirectPath = localStorage.getItem('ldirect')
        if (redirectPath) {
          localStorage.removeItem('ldirect')
          navigate(redirectPath)
        } else {
          navigate('/pricing')
        }
      }
    } catch (error) {
      console.error('Signup error:', error)

      if (error.response?.data?.invite_code) {
        const inviteError = Array.isArray(error.response.data.invite_code)
          ? error.response.data.invite_code[0]
          : error.response.data.invite_code

        // Check if error is about email-based invite requiring specific email
        const emailMatch = inviteError?.match(/only valid for ([^\s.]+(?:\.[^\s.]+)*)/)
        if (emailMatch) {
          setRequiredInviteEmail(emailMatch[1])
          setInviteError(inviteError)
        } else {
          setInviteError(inviteError)
          toast.error(inviteError || 'Invalid invite code')
        }
      } else if (error.response?.data?.email) {
        const emailError = Array.isArray(error.response.data.email)
          ? error.response.data.email[0]
          : error.response.data.email
        toast.error(emailError || 'Email already exists')
      } else if (error.response?.data?.password) {
        const passwordError = Array.isArray(error.response.data.password)
          ? error.response.data.password[0]
          : error.response.data.password
        toast.error(passwordError || 'Invalid password')
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to create account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div data-logo-box className="w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
          <p className="text-gray-600">Start your news journey today</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="John Doe"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => updateEmail(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${inviteError ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="you@example.com"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invite Code {inviteOnly && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => updateInviteCode(e.target.value.toUpperCase())}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent uppercase ${inviteError ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="ABC123XYZ"
                disabled={loading || checkingConfig}
              />
            </div>
            {inviteOnly && (
              <p className="text-xs text-gray-500 mt-1">
                Registration is currently invite-only. Please enter a valid invite code.
              </p>
            )}
            {!inviteOnly && (
              <p className="text-xs text-gray-500 mt-1">
                Optional: enter invite code if you have one
              </p>
            )}
            {inviteError && (
              <p className="text-xs text-red-500 mt-1">
                {inviteError}
              </p>
            )}
            {requiredInviteEmail && (
              <button
                type="button"
                onClick={() => setEmail(requiredInviteEmail)}
                className="text-xs text-blue-600 hover:text-blue-800 mt-1 text-left"
              >
                Use required email: {requiredInviteEmail}
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="••••••••"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Must be at least 12 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary mt-1"
            />
            <label className="ml-2 text-sm text-gray-600">
              I agree to the{' '}
              <Link to="/terms" className="text-primary hover:text-primary/80 font-medium">
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary hover:text-primary/80 font-medium">
                Privacy Policy
              </Link>
            </label>
          </div>

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={loading || checkingConfig}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating account...
              </>
            ) : checkingConfig ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        {/* Sign in link */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  )
}
